import DataTable from "@/components/Common/DataTable";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SMFItemType } from "@/consts/Estimate";
import { useSearchSmf } from "@/libs/react-query/hooks/useItemsheetsQuery";
import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { searchTableColumn } from "../columns/searchTableColumn";
import { SmfProduct } from "@/types/itemsheet";
import { useSelectedProjectInfo } from "../hooks/useTabStore";
import { useReadSpecType } from "@/libs/react-query/hooks/useSpecsQuery";
import { SpecType } from "@/types/specs";
import { showToast } from "@/utils/toastUtils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product_id: string | null;
  onApply: (selectedProduct: SmfProduct) => void;
};

export default function SearchProductDialog({
  open,
  onOpenChange,
  product_id,
  onApply,
}: Props) {
  const [searchValue, setSearchValue] = useState(product_id ?? "");
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );
  const [internalProducts, setInternalProducts] = useState<SmfProduct[]>([]);

  const selectedProjectInfo = useSelectedProjectInfo();
  const { projectId, projectType } = selectedProjectInfo || {};

  const getSpecTypeFromData = (type: SpecType | undefined): SMFItemType => {
    switch (type) {
      case SpecType.REAGENT:
        return SMFItemType.RGNT;
      case SpecType.COMMODITY:
        return SMFItemType.CMDTY;
      default:
        return SMFItemType.RGNT;
    }
  };

  const { data: specData } = useReadSpecType({
    project_id: projectId ?? "",
    project_type: projectType!,
  });

  const specType = getSpecTypeFromData(specData?.spec_type);

  const { data, refetch } = useSearchSmf({
    product_id: searchValue,
    item_type: specType,
    page: 0,
    limit: 100,
  });

  useEffect(() => {
    if (data?.products) {
      setInternalProducts(data.products);
    }
  }, [data]);

  useEffect(() => {
    if (open) {
      setSearchValue(product_id ?? "");
      setSelectedProductId(null);
    } else {
      setSearchValue("");
      setSelectedProductId(null);
      setInternalProducts([]);
    }
  }, [open, product_id]);

  const handleSearch = () => {
    if (searchValue.trim() === "") return;
    if (searchValue.trim().length < 3) {
      showToast({
        type: "error",
        message: "제품 번호는 최소 3자 이상 입력해야 합니다.",
      });
      return;
    }
    setSelectedProductId(null);
    refetch();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="min-w-[1000px] max-h-[750px] flex flex-col gap-6 overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-gray-A font-bold">
            제품 찾기
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-B font-normal" />
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <p className="text-xs text-gray-B font-bold">제품 번호</p>
            <div className="flex items-center gap-3">
              <Input
                placeholder="제품 번호를 입력하세요"
                className="w-full h-8"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <button
                type="button"
                className="flex-shrink-0 py-2 px-4 bg-dark-brown flex items-center gap-2 text-white text-xs font-bold rounded-sm"
                onClick={handleSearch}
              >
                <Search className="" size={12} />
                검색하기
              </button>
            </div>
          </div>

          <div className="border-t border-t-gray-D" />

          <DataTable
            data={internalProducts}
            columns={searchTableColumn}
            pageSize={10}
            onRowSelect={(row) => {
              setSelectedProductId(row?.id ?? null);
            }}
          />
        </div>
        <DialogFooter className="">
          <Button
            variant="cancel"
            onClick={() => {
              onOpenChange(false);
              setSearchValue("");
              setSelectedProductId(null);
            }}
          >
            취소하기
          </Button>
          <Button
            variant="confirm"
            onClick={() => {
              const selected = internalProducts.find(
                (p) => p.id === selectedProductId
              );
              if (selected) {
                onApply(selected);
                onOpenChange(false);
                setSearchValue("");
                setSelectedProductId(null);
              }
            }}
          >
            적용하기
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
